import apiClient, { handleApiError } from '../lib/api-client';
import { API_ENDPOINTS } from '../config/api';

export interface Doctor {
  id: string;
  userId: string;
  licenseNumber: string;
  specialization: string;
  qualifications: string[];
  experience: number;
  consultationFee: number;
  isVerified: boolean;
  isApproved: boolean;
  rating?: number;
  user?: {
    profile?: {
      displayName?: string;
      avatarUrl?: string;
    };
  };
}

export interface Appointment {
  id: string;
  userId: string;
  doctorId: string;
  scheduledAt: string;
  duration: number;
  status: 'SCHEDULED' | 'CONFIRMED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
  reason?: string;
  doctor?: Doctor;
}

export interface Availability {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
}

export const telehealthService = {
  async getDoctors(params?: { limit?: number; offset?: number }): Promise<Doctor[]> {
    try {
      const response = await apiClient.get<{ data: { data: Doctor[] } }>(
        API_ENDPOINTS.TELEHEALTH.DOCTORS,
        { params }
      );
      return response.data.data?.data || response.data.data || [];
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async getDoctor(id: string): Promise<Doctor> {
    try {
      const response = await apiClient.get<{ data: Doctor }>(API_ENDPOINTS.TELEHEALTH.DOCTOR(id));
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async bookAppointment(data: {
    doctorId: string;
    scheduledAt: string;
    duration?: number;
    reason?: string;
  }): Promise<Appointment> {
    try {
      const response = await apiClient.post<{ data: Appointment }>(
        API_ENDPOINTS.TELEHEALTH.APPOINTMENTS,
        data
      );
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async getAppointments(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<Appointment[]> {
    try {
      const response = await apiClient.get<{ data: Appointment[] }>(
        API_ENDPOINTS.TELEHEALTH.APPOINTMENTS,
        { params }
      );
      return response.data.data || response.data || [];
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async cancelAppointment(id: string, reason?: string) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.TELEHEALTH.CANCEL_APPOINTMENT(id), {
        reason,
      });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },
};

